import { readProblem } from '../../components/data-grid/problem';
import { ApiError, requestJson } from '../documents/api';
import type { FetchLike, LocalizedText } from '../documents/api';
import type { DocumentStatus } from '../documents/filters';
import type {
  AccountOption,
  DocumentFormState,
  DocumentTypeView,
  FieldDefinitionView,
  FieldType,
  FormLine,
  StatusTransitionView,
} from './form';
import type { AttachmentView, ChainGraphView } from './DocumentFormView';

export type { FetchLike };

export interface DocumentTypeDto {
  code: string;
  module: string;
  nameI18n: LocalizedText;
  fields: FieldDefinitionView[];
  tableFields: { fields: FieldDefinitionView[]; maxRows?: number };
  statusModel: {
    approvalEnabled: boolean;
    transitions: StatusTransitionView[];
  };
  operations: Array<{ code: string; nameI18n: LocalizedText }>;
}

export interface DocumentLineDto {
  id: string;
  lineNo: number;
  data: Record<string, unknown>;
  amount: string;
  dimensions: Record<string, string>;
}

export interface DocumentDto {
  id: string;
  type: string;
  operationKind: string;
  orgId: string;
  number: string;
  date: string;
  status: DocumentStatus;
  author: string;
  data: Record<string, unknown>;
  lines: DocumentLineDto[];
  basedOnDocId?: string | null;
}

export interface AccountDto {
  id: string;
  code: string;
  nameAz: string;
  nameEn: string;
  nameRu: string;
  isGroup: boolean;
  isOffbalance: boolean;
  dimensionRules: Array<{ slot: number; dimType: string; required?: boolean }>;
}

interface AccountPageDto {
  items: AccountDto[];
  total: number;
}

export interface AttachmentDto {
  id: string;
  documentId: string;
  fileName: string;
  mimeType: string;
  size: number;
  checksum: string;
  createdAt: string;
  createdBy?: string | null;
}

export interface ChainGraphDto {
  documentId: string;
  up: Array<{ documentId: string; number: string; type: string; status: DocumentStatus }>;
  down: Array<{ documentId: string; number: string; type: string; status: DocumentStatus }>;
}

export function localizedName(text: LocalizedText | undefined, language: 'az' | 'en' | 'ru'): string {
  if (text === undefined) {
    return '';
  }
  return text[language] ?? text.az ?? text.en ?? '';
}

export async function loadDocumentTypes(fetcher: FetchLike): Promise<DocumentTypeDto[]> {
  return requestJson<DocumentTypeDto[]>(fetcher, '/document-types');
}

export async function loadDocument(fetcher: FetchLike, id: string): Promise<DocumentDto> {
  return requestJson<DocumentDto>(fetcher, `/documents/${id}`);
}

export async function loadAccounts(fetcher: FetchLike): Promise<AccountDto[]> {
  const page = await requestJson<AccountPageDto>(fetcher, '/accounts?pageSize=1000');
  return page.items;
}

export async function loadChain(fetcher: FetchLike, id: string): Promise<ChainGraphView> {
  return requestJson<ChainGraphView>(fetcher, `/documents/${id}/chain`);
}

export async function loadAttachments(fetcher: FetchLike, id: string): Promise<AttachmentDto[]> {
  return requestJson<AttachmentDto[]>(fetcher, `/documents/${id}/attachments`);
}

export type DocumentAction = () => Promise<DocumentDto>;

export interface SaveDocumentInput {
  actor: string;
  orgId: string;
  type: string;
  operationKind: string;
  date: string;
  number?: string;
  data: Record<string, unknown>;
  lines: Array<{ data: Record<string, unknown>; amount: string; dimensions?: Record<string, string> }>;
}

export async function createDocument(fetcher: FetchLike, input: SaveDocumentInput): Promise<DocumentDto> {
  return requestJson<DocumentDto>(fetcher, '/documents', {
    method: 'POST',
    body: JSON.stringify({ ...input, author: input.actor }),
  });
}

export async function patchDocument(
  fetcher: FetchLike,
  id: string,
  input: { actor: string; operationKind: string; data: Record<string, unknown>; lines: SaveDocumentInput['lines'] },
): Promise<DocumentDto> {
  return requestJson<DocumentDto>(fetcher, `/documents/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export async function approveDocument(fetcher: FetchLike, id: string, actor: string): Promise<DocumentDto> {
  return requestJson<DocumentDto>(fetcher, `/documents/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ actor }),
  });
}

export async function postDocument(fetcher: FetchLike, id: string, actor: string): Promise<DocumentDto> {
  return requestJson<DocumentDto>(fetcher, `/documents/${id}/post`, {
    method: 'POST',
    body: JSON.stringify({ actor }),
  });
}

export async function cancelDocument(
  fetcher: FetchLike,
  id: string,
  actor: string,
  reason: string,
): Promise<DocumentDto> {
  return requestJson<DocumentDto>(fetcher, `/documents/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ actor, reason }),
  });
}

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('read-failed'));
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

export async function uploadAttachment(
  fetcher: FetchLike,
  documentId: string,
  file: File,
): Promise<AttachmentDto> {
  const contentBase64 = await toBase64(file);
  return requestJson<AttachmentDto>(fetcher, `/documents/${documentId}/attachments`, {
    method: 'POST',
    body: JSON.stringify({
      fileName: file.name,
      mimeType: file.type === '' ? 'application/octet-stream' : file.type,
      size: file.size,
      contentBase64,
    }),
  });
}

export async function deleteAttachment(fetcher: FetchLike, attachmentId: string): Promise<void> {
  const response = await fetcher(`/attachments/${attachmentId}`, { method: 'DELETE' });
  if (!response.ok) {
    throw new ApiError(await readProblem(response));
  }
}

export function toFormState(document: DocumentDto): DocumentFormState {
  return {
    id: document.id,
    type: document.type,
    operationKind: document.operationKind,
    number: document.number,
    date: document.date,
    status: document.status,
    data: document.data,
    lines: document.lines.map((line, index) => ({
      key: line.id === '' ? `line-${index}` : line.id,
      lineNo: line.lineNo,
      data: line.data,
      amount: line.amount,
      dimensions: line.dimensions,
    })),
  };
}

export function emptyFormState(type: DocumentTypeDto, date: string): DocumentFormState {
  return {
    type: type.code,
    operationKind: type.operations[0]?.code ?? '',
    number: '',
    date,
    status: 'draft',
    data: {},
    lines: [],
  };
}

const FIELD_TYPES: FieldType[] = [
  'string',
  'text',
  'number',
  'amount',
  'quantity',
  'date',
  'boolean',
  'catalog',
  'enum',
];

function normalizeField(field: FieldDefinitionView): FieldDefinitionView {
  return { ...field, type: FIELD_TYPES.includes(field.type) ? field.type : 'string' };
}

export function toTypeView(type: DocumentTypeDto, language: 'az' | 'en' | 'ru'): DocumentTypeView {
  return {
    code: type.code,
    name: localizedName(type.nameI18n, language),
    fields: type.fields.map(normalizeField),
    tableFields: {
      fields: type.tableFields.fields.map(normalizeField),
      ...(type.tableFields.maxRows === undefined ? {} : { maxRows: type.tableFields.maxRows }),
    },
    statusModel: {
      approvalEnabled: type.statusModel.approvalEnabled,
      transitions: type.statusModel.transitions,
    },
    operations: type.operations.map((operation) => ({
      code: operation.code,
      name: localizedName(operation.nameI18n, language),
    })),
  };
}

function accountName(account: AccountDto, language: 'az' | 'en' | 'ru'): string {
  if (language === 'en') {
    return account.nameEn;
  }
  if (language === 'ru') {
    return account.nameRu;
  }
  return account.nameAz;
}

export function toAccountOptions(accounts: readonly AccountDto[], language: 'az' | 'en' | 'ru'): AccountOption[] {
  return accounts.map((account) => ({
    id: account.id,
    code: account.code,
    name: accountName(account, language),
    isGroup: account.isGroup,
    isOffbalance: account.isOffbalance,
    dimensionRules: account.dimensionRules,
  }));
}

export function toAttachmentViews(attachments: readonly AttachmentDto[]): AttachmentView[] {
  return attachments.map((attachment) => ({
    id: attachment.id,
    fileName: attachment.fileName,
    mimeType: attachment.mimeType,
    size: attachment.size,
    checksum: attachment.checksum,
    createdAt: attachment.createdAt,
    uploadedBy: attachment.createdBy ?? null,
  }));
}

export function linesToInput(lines: readonly FormLine[]): SaveDocumentInput['lines'] {
  return lines.map((line) => ({
    data: line.data,
    amount: line.amount,
    ...(Object.keys(line.dimensions).length === 0 ? {} : { dimensions: line.dimensions }),
  }));
}
